import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';

import {makeStyles} from 'tss-react/mui';
import {ApiButton, Box, TextFieldForm, todayDate, Typography} from 'web-library';
import ControlledAutocomplete from '../../../../components/form-items/ControlledAutocomplete';
import {ID_TYPE_MENU_ITEMS} from '../../../accountManagement/logic/useCreateCandidateForm';
import {
  RetrieveAppealRegistrationFormData,
  schemaRetrieveAppealRegistrationDetails
} from './schema/schemaRetrieveAppealRegistrationDetails';
import {LookupListApiItem} from "../../../../api/user/model";
import {useForm} from "react-hook-form";
import {joiResolver} from "@hookform/resolvers/joi";
import {usePostRetrieveSchoolCandidateAppealRegistration} from "../../../../api-refactored/school-candidate-payment/usePostRetrieveSchoolCandidateAppealRegistration";
import {useGetSchoolsForSchoolCandidatePayment} from "../../../../api-refactored/school-candidate-payment/useGetSchoolsForSchoolCandidatePayment";
import SchoolCandidateAppealPaymentDetails from "./SchoolCandidateAppealPaymentDetails";

const useStyles = makeStyles()((theme) =>
    ({
      input: {
        marginBottom: theme.spacing(4),
      },
      dropdownTextField: {
        marginBottom: theme.spacing(3),
      },
      halfWidthLeftItem: {
        width: '50%',
        paddingRight: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
          width: '100%',
          paddingRight: 0,
        },
      },
      errorText: {
        color: theme.color.state.stateError,
        marginBottom: theme.spacing(2),
      },
      halfWidthRightItem: {
        width: '50%',
        paddingLeft: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
          width: '100%',
          paddingLeft: 0,
        },
      },
      responsiveFlexContainer: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
      },
    }));
const RetrieveAppealRegistrationForm = () => {
  const {classes} = useStyles();
  const {t} = useTranslation();
  const date = todayDate;
  const examYearOptionsStart = date.getFullYear() - 1;
  const examYearOptions = [...Array(2).keys()].map((i) => i + examYearOptionsStart);

  const [errorMessage, setErrorMessage] = useState('');
  const form = useForm<RetrieveAppealRegistrationFormData>({
    mode: 'onSubmit',
    resolver: joiResolver(schemaRetrieveAppealRegistrationDetails),
  });

  const {
    data: appealRegistration,
    mutate: postRetrieveAppealRegistrationMutation,
    status: postRetrieveAppealRegistrationApiStatus,
  } = usePostRetrieveSchoolCandidateAppealRegistration({
    onSuccess: () => {
      setErrorMessage('');
    },
    onError: (err) => {
      if (err.response?.status === 403 && err.response?.data?.code !== "1007") {
        setErrorMessage(t('profile->>LoginForm.PleaseRefreshThePage'));
      } else {
        setErrorMessage(err.response?.data.message ?? '');
      }
    },
  });

  const {data: schools} = useGetSchoolsForSchoolCandidatePayment({});
  const [candidateInfo, setCandidateInfo] = useState<RetrieveAppealRegistrationFormData>();
  const {handleSubmit, control, formState: {errors}} = form;
  const handleRetrieveAppealRegistrationFormSubmission = (formData: RetrieveAppealRegistrationFormData) => {
    postRetrieveAppealRegistrationMutation({
      idType: formData.IdType === 'P' ? 'PASSPORT' : 'NRIC_OR_FIN',
      uinFinPassport: formData.uinFinPassport,
      schoolUuid: formData.school.code,
      examYear: formData.examYear,
      email: formData.email,
    });
    setCandidateInfo(formData);
  };

  return (
      <>
        <form onSubmit={handleSubmit(handleRetrieveAppealRegistrationFormSubmission)}>
          <Box className={[classes.input, classes.responsiveFlexContainer].join(' ')}>
            <ControlledAutocomplete<{ key: string, value: string, children: string }, false, false, undefined>
                label={t('components->>SchoolCandidateAppealPayment.IdType')}
                className={classes.halfWidthLeftItem}
                isRequired
                control={control}
                name="IdType"
                id="inputSelect-idType"
                error={!!errors.IdType}
                helperText={errors?.IdType?.message?.toString()}
                options={ID_TYPE_MENU_ITEMS}
                getOptionLabel={({children}) => children}
                placeholder={t('components->>SchoolCandidateAppealPayment.IdTypePlaceHolder')}
                transform={{
                  input: (selectedOption, options) =>
                      options.find((option) => option.value === selectedOption) ?? null,
                  output: (selectedOption) => (selectedOption ? selectedOption.value : null),
                }}
            />
            <TextFieldForm
                form={form}
                className={classes.halfWidthRightItem}
                label={t('components->>SchoolCandidateAppealPayment.NRIC/FIN/Passport No.')}
                isRequired
                name="uinFinPassport"
                type="text"
                id="inputText-candidate-nric-fin-pass-no"
                placeholder={t('components->>SchoolCandidateAppealPayment.nricFinPassportPlaceHolder')}
                size="medium"
                error={!!errors.uinFinPassport}
                helperText={errors?.uinFinPassport?.message?.toString()}
                onChange={(e) => form.setValue('uinFinPassport', e.target.value.toUpperCase())}
                fullWidth
            />
          </Box>
          <ControlledAutocomplete<LookupListApiItem, false, false, undefined>
              label={t('components->>SchoolCandidateAppealPayment.School')}
              className={classes.input}
              isRequired
              control={control}
              name="school"
              id="inputSelect-school"
              error={!!errors.school}
              helperText={errors?.school?.message?.toString()}
              options={schools ?? []}
              getOptionLabel={({description}) => description}
              placeholder={t('components->>SchoolCandidateAppealPayment.SchoolPlaceHolder')}
          />
          <ControlledAutocomplete
              label={t('components->>SchoolCandidateAppealPayment.ExamYear')}
              className={classes.input}
              isRequired
              control={control}
              name="examYear"
              id="inputSelect-examYear"
              error={!!errors.examYear}
              helperText={errors?.examYear?.message?.toString()}
              options={examYearOptions}
              getOptionLabel={(selected) => selected.toString()}
          />
          <TextFieldForm
              form={form}
              className={classes.input}
              label={t('components->>SchoolCandidateAppealPayment.Email')}
              isRequired
              subLabel={t('components->>SchoolCandidateAppealPayment.EmailAwareness')}
              name="email"
              type="text"
              id="inputText-email"
              placeholder={t('components->>SchoolCandidateAppealPayment.EmailPlaceholder')}
              size="medium"
              error={!!errors.email}
              helperText={errors?.email?.message?.toString()}
              fullWidth
          />
          {errorMessage && (
              <Typography className={classes.errorText} variant="body2">
                {errorMessage}
              </Typography>
          )}
          <ApiButton
              status={postRetrieveAppealRegistrationApiStatus}
              successLabel="Retrieved"
              errorLabel="Unable to Retrieve"
              type="submit"
              className={classes.input}
              successTimeout={1000000}
              id="button-retrieve-candidate-payment"
          >
            {t('components->>SchoolCandidateAppealPayment.RetrieveDetails')}
          </ApiButton>
        </form>

        {appealRegistration && candidateInfo && (
            <SchoolCandidateAppealPaymentDetails
                appealRegistration={appealRegistration}
                candidateInfo={candidateInfo}
            />
        )}
      </>
  );
};

export default RetrieveAppealRegistrationForm;
